
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, DollarSign, PieChart, BarChart3 } from 'lucide-react';
import { generateRealTimeLoanPrograms } from '../utils/realTimeLoanPrograms';
import { calculateLoanProgramValues } from '../utils/mortgageUtils';
import { PaymentBreakdownTab } from './PaymentBreakdownTab';
import { AmortizationTab } from './AmortizationTab';
import { CostScenariosTab } from './CostScenariosTab';
import { EquityGrowthTab } from './EquityGrowthTab';
import { calculateEnergyEfficiencyImpact } from '@/utils/analytics/energyEfficiencyCalculator';
import { EnergyEfficiencyCard } from '@/components/calculator/advanced/analytics/EnergyEfficiencyCard';
import type { MortgageData } from '../../CalculatorTabs';

interface AnalyticsViewProps {
  selectedTerm: string;
  mortgageData: MortgageData;
}

export const AnalyticsView: React.FC<AnalyticsViewProps> = ({ selectedTerm, mortgageData }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [hersIndex, setHersIndex] = useState(75);
  const [baselineHers, setBaselineHers] = useState(100);
  const [annualUtilityCost, setAnnualUtilityCost] = useState(2500);

  // Use real mortgage data calculations
  const loanPrograms = generateRealTimeLoanPrograms(mortgageData);
  const termYears = parseInt(selectedTerm);
  const calculatedPrograms = loanPrograms.map(program => 
    calculateLoanProgramValues(program, termYears, mortgageData)
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const bestProgram = calculatedPrograms.find(p => p.recommended) || calculatedPrograms[0];
  const totalSavings = calculatedPrograms.reduce((sum, program) => {
    return sum + Math.max(0, calculatedPrograms[0].totalCost - program.totalCost);
  }, 0);

  const energyImpact = calculateEnergyEfficiencyImpact(
    mortgageData,
    hersIndex,
    baselineHers,
    annualUtilityCost
  );

  return (
    <div className="space-y-6">
      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-900/20 to-blue-800/20 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-400">Home Price</p>
                <p className="text-2xl font-bold text-blue-300">
                  {formatCurrency(mortgageData.homePrice)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-900/20 to-green-800/20 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-400">Best Monthly</p>
                <p className="text-2xl font-bold text-green-300">
                  {formatCurrency(bestProgram.monthlyPayment)}
                </p>
              </div>
              <TrendingDown className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-900/20 to-purple-800/20 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-400">Programs Available</p>
                <p className="text-2xl font-bold text-purple-300">
                  {calculatedPrograms.length}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-900/20 to-orange-800/20 border-orange-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-400">Credit Score</p>
                <p className="text-2xl font-bold text-orange-300">
                  {mortgageData.creditScore}
                </p>
              </div>
              <PieChart className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 bg-slate-900/60 border border-slate-700">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="breakdown" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300"
          >
            Payment Breakdown
          </TabsTrigger>
          <TabsTrigger 
            value="amortization" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300"
          >
            Amortization
          </TabsTrigger>
          <TabsTrigger 
            value="scenarios" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300"
          >
            Cost Scenarios
          </TabsTrigger>
          <TabsTrigger 
            value="equity" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300"
          >
            Equity Growth
          </TabsTrigger>
          <TabsTrigger 
            value="energy" 
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-slate-300"
          >
            Energy Efficiency
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          {/* Detailed Analytics Table */}
          <Card className="bg-slate-900/60 border-slate-700">
            <CardHeader>
              <CardTitle className="text-slate-100">Comprehensive Loan Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-3 px-2 text-slate-300">Program</th>
                      <th className="text-right py-3 px-2 text-slate-300">Monthly Payment</th>
                      <th className="text-right py-3 px-2 text-slate-300">Down Payment</th>
                      <th className="text-right py-3 px-2 text-slate-300">APR</th>
                      <th className="text-right py-3 px-2 text-slate-300">Total Cost</th>
                      <th className="text-right py-3 px-2 text-slate-300">Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedPrograms.map((program) => {
                      const savings = calculatedPrograms[0].totalCost - program.totalCost;
                      
                      return (
                        <tr key={program.id} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-200">{program.name}</span>
                              {program.recommended && (
                                <Badge variant="outline" className="bg-green-500/20 border-green-500/50 text-green-300 text-xs">
                                  Recommended
                                </Badge>
                              )}
                              {!program.eligibleForUser && (
                                <Badge variant="outline" className="bg-red-500/20 border-red-500/50 text-red-300 text-xs">
                                  Not Eligible
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right text-slate-200 font-medium">
                            {formatCurrency(program.monthlyPayment)}
                          </td>
                          <td className="py-3 px-2 text-right text-slate-200">
                            {formatCurrency(program.downPayment)}
                            <div className="text-xs text-slate-400">({program.downPercent}%)</div>
                          </td>
                          <td className="py-3 px-2 text-right text-slate-200">
                            {program.apr.toFixed(2)}%
                          </td>
                          <td className="py-3 px-2 text-right text-slate-200 font-medium">
                            {formatCurrency(program.totalCost)}
                          </td>
                          <td className="py-3 px-2 text-right">
                            {savings > 0 ? (
                              <span className="text-green-400 flex items-center justify-end gap-1">
                                <TrendingDown className="w-3 h-3" />
                                {formatCurrency(savings)}
                              </span>
                            ) : savings < 0 ? (
                              <span className="text-red-400 flex items-center justify-end gap-1">
                                <TrendingUp className="w-3 h-3" />
                                {formatCurrency(Math.abs(savings))}
                              </span>
                            ) : (
                              <span className="text-slate-400">Baseline</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="breakdown" className="mt-6">
          <PaymentBreakdownTab selectedTerm={selectedTerm} mortgageData={mortgageData} />
        </TabsContent>

        <TabsContent value="amortization" className="mt-6">
          <AmortizationTab mortgageData={mortgageData} />
        </TabsContent>

        <TabsContent value="scenarios" className="mt-6">
          <CostScenariosTab selectedTerm={selectedTerm} mortgageData={mortgageData} />
        </TabsContent>

        <TabsContent value="equity" className="mt-6">
          <EquityGrowthTab selectedTerm={selectedTerm} mortgageData={mortgageData} />
        </TabsContent>

        <TabsContent value="energy" className="mt-6">
          <EnergyEfficiencyCard 
            impact={energyImpact}
            onHersIndexChange={setHersIndex}
            onBaselineChange={setBaselineHers}
            onUtilityCostChange={setAnnualUtilityCost}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
